import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  TextField, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle, 
  IconButton, 
  Tooltip,
  Alert,
  Chip,
  CircularProgress,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocationOn from '@mui/icons-material/LocationOn';
import GridOn from '@mui/icons-material/GridOn';
import Layers from '@mui/icons-material/Layers';
import api from '../services/api.service';
import { useError } from '../contexts/ErrorContext';
import RackManager from './RackManager';
import BayManager from './BayManager';

/**
 * Warehouse Manager Component
 * 
 * Provides a UI for managing warehouses and zones
 */
function WarehouseManager() {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openWarehouseDialog, setOpenWarehouseDialog] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [warehouseToDelete, setWarehouseToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [warehouseForm, setWarehouseForm] = useState({
    name: '',
    description: '',
    address: '',
    isActive: true
  });
  const { setError: setGlobalError } = useError();

  // Load warehouses on component mount
  const loadWarehouses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/inventories/warehouses');
      
      // Handle different response formats
      let warehouseData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          warehouseData = response.data;
        } else if (response.data.data && response.data.data.warehouses && Array.isArray(response.data.data.warehouses)) {
          // Handle nested structure: { success: true, data: { warehouses: [...] } }
          warehouseData = response.data.data.warehouses;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          warehouseData = response.data.data;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          warehouseData = response.data.items;
        } else if (response.data.warehouses && Array.isArray(response.data.warehouses)) {
          warehouseData = response.data.warehouses;
        } else if (response.data.success && response.data.data) {
          warehouseData = Array.isArray(response.data.data) ? response.data.data : [];
        }
      }
      
      setWarehouses(warehouseData);
    } catch (err) {
      console.error('Warehouse loading error:', err);
      setError('Failed to load warehouses: ' + (err.response?.data?.error || err.message));
      // Set empty array on error to prevent map errors
      setWarehouses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setWarehouseForm({
      ...warehouseForm,
      [name]: value
    });
  };

  // Open warehouse dialog for adding a new warehouse
  const handleAddWarehouse = () => {
    setEditingWarehouse(null);
    setWarehouseForm({
      name: '',
      description: '',
      address: '',
      isActive: true
    });
    setOpenWarehouseDialog(true);
  };

  // Open warehouse dialog for editing an existing warehouse
  const handleEditWarehouse = (warehouse) => {
    setEditingWarehouse(warehouse);
    setWarehouseForm({
      name: warehouse.name,
      description: warehouse.description || '',
      address: warehouse.address || '',
      isActive: warehouse.isActive !== false
    });
    setOpenWarehouseDialog(true);
  };

  // Close warehouse dialog
  const handleCloseDialog = () => {
    setOpenWarehouseDialog(false);
  };

  // Save warehouse (create or update)
  const handleSaveWarehouse = async () => {
    try {
      setError(null);
      
      // Validate form
      if (!warehouseForm.name.trim()) {
        setError('Warehouse name is required');
        return;
      }

      setLoading(true);

      if (editingWarehouse) {
        // Update existing warehouse
        await api.put(`/inventories/warehouses/${editingWarehouse.id}`, warehouseForm);
        setSuccess('Warehouse updated successfully');
      } else {
        // Create new warehouse
        await api.post('/inventories/warehouses', warehouseForm);
        setSuccess('Warehouse created successfully');
      }

      // Reload warehouses
      await loadWarehouses();
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${editingWarehouse ? 'update' : 'create'} warehouse`);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete warehouse
  const handleDeleteWarehouse = (warehouse) => {
    setWarehouseToDelete(warehouse);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setError(null);
      setLoading(true);
      await api.delete(`/inventories/warehouses/${warehouseToDelete.id}`);
      setSuccess('Warehouse deleted successfully');
      setDeleteDialogOpen(false);
      setWarehouseToDelete(null);
      await loadWarehouses();
    } catch (err) {
      if (err.response?.data?.racksCount > 0) {
        setError(`Cannot delete warehouse: It has ${err.response.data.racksCount} racks. Please delete or reassign racks first.`);
      } else if (err.response?.data?.itemsCount > 0) {
        setError(`Cannot delete warehouse: It has ${err.response.data.itemsCount} items. Please reassign items first.`);
      } else {
        setError(err.response?.data?.error || 'Failed to delete warehouse');
      }
      setDeleteDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  // Toggle warehouse active status
  const handleToggleActive = async (warehouse) => {
    try {
      await api.put(`/inventories/warehouses/${warehouse.id}`, {
        ...warehouse,
        isActive: !warehouse.isActive
      });
      await loadWarehouses();
    } catch (err) {
      setError('Failed to update warehouse status');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<WarehouseIcon />} label="Warehouses" />
        <Tab icon={<GridOn />} label="Racks" />
        <Tab icon={<Layers />} label="Bays" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {activeTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" component="h2">
              <WarehouseIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Manage Warehouses
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={handleAddWarehouse}
              disabled={loading}
            >
              Add Warehouse
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : warehouses.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <WarehouseIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Warehouses Found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create your first warehouse to start organizing inventory
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddWarehouse}
                >
                  Create Warehouse
                </Button>
              </CardContent>
            </Card>
          ) : (
            <TableContainer component={Paper}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Items</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(warehouses) && warehouses.map((warehouse) => (
                    <TableRow key={warehouse.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WarehouseIcon fontSize="small" color="action" />
                          <Typography variant="subtitle2">{warehouse.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{warehouse.description || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={warehouse.isActive ? 'Active' : 'Inactive'}
                          color={warehouse.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={warehouse.itemsCount || 0} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Toggle Active Status">
                          <Switch
                            checked={warehouse.isActive !== false}
                            onChange={() => handleToggleActive(warehouse)}
                            size="small"
                          />
                        </Tooltip>
                        <Tooltip title="Edit Warehouse">
                          <IconButton
                            size="small"
                            onClick={() => handleEditWarehouse(warehouse)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Warehouse">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteWarehouse(warehouse)}
                            disabled={warehouse.itemsCount > 0}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <RackManager />
      )}
      {activeTab === 2 && (
        <BayManager />
      )}

      {/* Warehouse Dialog */}
      <Dialog open={openWarehouseDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingWarehouse ? 'Edit Warehouse' : 'Add New Warehouse'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Warehouse Name"
                name="name"
                value={warehouseForm.name}
                onChange={handleInputChange}
                required
                autoFocus
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={warehouseForm.description}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={warehouseForm.isActive}
                    onChange={(e) => setWarehouseForm({ ...warehouseForm, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveWarehouse} 
            variant="contained"
            disabled={loading}
          >
            {editingWarehouse ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the warehouse "{warehouseToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WarehouseManager;